import type { PiV2Bundle, PiV2Plan } from './typesV2';
import { PI_V2_ROLE_LABEL, piV2EligibilityText, piV2Money } from './utilsV2';

/**
 * Print Insurance Summary (spec §21). Builds a clean, print-optimised document
 * from the resolved bundle and prints that — not `window.print()` of the app.
 * Opportunities are intentionally excluded (§21.1): they are an internal
 * operational tool and can contain production estimates or another family
 * member's recall.
 */

export interface PrintInsuranceSummaryOptions {
  patientName: string;
}

function esc(s: string): string {
  const map: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  };
  return s.replace(/[&<>"']/g, (c) => map[c] ?? c);
}

function planBlock(plan: PiV2Plan): string {
  const elig = piV2EligibilityText(plan.eligibility);
  const b = plan.benefits;
  const benefit =
    b.annualMax > 0
      ? `${piV2Money(b.remaining)} remaining · ${piV2Money(b.used)} used of ${piV2Money(
          b.annualMax,
        )} annual maximum · Deductible ${piV2Money(b.deductibleMet)} of ${piV2Money(
          b.deductible,
        )} met`
      : 'No annual maximum — copay/discount plan';
  return `<div class="plan">
    <div class="plan-h">
      <span class="role">${esc(PI_V2_ROLE_LABEL[plan.role])} · ${esc(plan.planType)}</span>
      <b>${esc(plan.carrier)}</b> — ${esc(plan.planName)}
      <span class="net">${plan.inNetwork ? 'In-network' : 'Out-of-network'}</span>
    </div>
    <div class="kv">
      <span>Member ID</span><b>${esc(plan.memberId)}</b>
      <span>Group</span><b>${esc(plan.group)}</b>
      <span>Subscriber</span><b>${esc(plan.subscriber)} (${esc(plan.relationship)})</b>
      <span>Effective</span><b>${esc(plan.effective)}</b>
      <span>Carrier phone</span><b>${esc(plan.carrierPhone)}</b>
      <span>Verification</span><b>${esc(elig.label)}</b>
    </div>
    <div class="benefit">${benefit}</div>
  </div>`;
}

export function buildInsuranceSummaryDoc(
  bundle: PiV2Bundle,
  opts: PrintInsuranceSummaryOptions,
): string {
  const printedAt = new Date().toLocaleString();
  const plans = bundle.plans.map(planBlock).join('');

  const cob =
    bundle.coordinationNote != null && bundle.coordinationNote !== '' && bundle.plans.length > 1
      ? `<div class="cob"><b>Coordination of benefits:</b> ${esc(bundle.coordinationNote)}</div>`
      : '';

  let estimate = '';
  if (bundle.visit) {
    const v = bundle.visit;
    const a = v.appointment;
    if (v.estimate.estimateIsReal) {
      const rows = v.estimate.lines
        .map(
          (l) => `<tr>
            <td class="mono">${esc(l.code)}</td>
            <td>${esc(l.label)}</td>
            <td class="n">${piV2Money(l.officeFee)}</td>
            <td class="n">${piV2Money(l.insuranceEstimate)}</td>
            <td class="n b">${piV2Money(l.patientEstimate)}</td>
          </tr>`,
        )
        .join('');
      estimate = `<h2>Estimate for this visit</h2>
        <div class="muted">${esc(a.whenLabel)} · ${esc(a.provider)} · ${esc(a.procedureLabel)}</div>
        <div class="hero"><span class="big">${piV2Money(
          v.estimate.totals.patientEstimate,
        )}</span> estimated patient portion · <span class="mono">${piV2Money(
          v.estimate.totals.officeFee,
        )} fee − ${piV2Money(v.estimate.totals.insuranceEstimate)} estimated insurance</span></div>
        <table><thead><tr><th></th><th>Procedure</th><th class="n">Office fee</th><th class="n">Est. insurance</th><th class="n">Est. patient</th></tr></thead><tbody>
        ${rows}
        <tr class="tot"><td></td><td>Totals</td><td class="n">${piV2Money(
          v.estimate.totals.officeFee,
        )}</td><td class="n">${piV2Money(v.estimate.totals.insuranceEstimate)}</td><td class="n b">${piV2Money(
          v.estimate.totals.patientEstimate,
        )}</td></tr></tbody></table>
        <div class="disc">Estimates are not a guarantee of payment. Final insurance payment and patient responsibility may vary.</div>`;
    } else {
      estimate = `<h2>Estimate for this visit</h2>
        <div class="muted">${esc(a.whenLabel)} · ${esc(a.provider)}</div>
        <div class="warn">${esc(
          v.estimate.unavailableReason ??
            'Eligibility is not confirmed, so no estimate can be shown.',
        )}</div>`;
    }
  }

  const tp = bundle.treatmentPlan;
  const treatment =
    tp.rows.length > 0
      ? `<h2>Treatment plan coverage</h2>
        <table><thead><tr><th>Procedure</th><th>Status</th><th class="n">Fee</th><th class="n">Est. insurance</th><th class="n">Est. patient</th></tr></thead><tbody>
        ${tp.rows
          .map(
            (r) => `<tr>
              <td>${esc(r.procedure)}</td>
              <td>${esc(r.status)}</td>
              <td class="n">${piV2Money(r.total)}</td>
              <td class="n">${piV2Money(r.insurance)}</td>
              <td class="n b">${piV2Money(r.patient)}</td>
            </tr>`,
          )
          .join('')}
        <tr class="tot"><td>Totals</td><td></td><td class="n">${piV2Money(tp.total)}</td><td class="n">${piV2Money(
          tp.insurance,
        )}</td><td class="n b">${piV2Money(tp.patient)}</td></tr></tbody></table>`
      : '';

  return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>ELVA Insurance Summary — ${esc(opts.patientName)}</title>
<style>
  * { box-sizing: border-box; }
  body { margin: 0; padding: 32px 36px; color: #14203a; font: 13px/1.5 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; }
  .hd { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; }
  h1 { margin: 0; font-size: 20px; }
  h2 { margin: 20px 0 6px; font-size: 14px; }
  .kicker { text-transform: uppercase; letter-spacing: .08em; font-size: 10px; font-weight: 700; color: #64748b; }
  .gen { font-size: 11px; color: #94a3b8; text-align: right; }
  .muted { color: #64748b; font-size: 12px; }
  .mono { font-variant-numeric: tabular-nums; }
  hr { border: none; border-top: 1px solid #e3e8f0; margin: 14px 0; }
  .plan { border: 1px solid #e3e8f0; border-radius: 8px; padding: 10px 12px; margin-top: 8px; break-inside: avoid; }
  .plan-h { font-size: 13px; }
  .plan-h .role { text-transform: uppercase; letter-spacing: .06em; font-size: 10px; font-weight: 700; color: #64748b; margin-right: 6px; }
  .plan-h .net { color: #12855f; font-size: 11px; font-weight: 700; margin-left: 6px; }
  .kv { display: grid; grid-template-columns: auto 1fr auto 1fr; gap: 3px 10px; margin-top: 6px; font-size: 11.5px; }
  .kv span { color: #64748b; } .kv b { color: #14203a; }
  .benefit { margin-top: 6px; font-size: 12px; color: #12855f; font-weight: 600; }
  .cob { margin-top: 10px; background: #f5f8ff; border: 1px solid #e3e8f0; border-radius: 8px; padding: 8px 12px; font-size: 12.5px; }
  .hero { margin: 4px 0 8px; } .hero .big { font-size: 24px; font-weight: 800; color: #0f8a86; }
  table { width: 100%; border-collapse: collapse; margin-top: 4px; }
  th, td { padding: 6px 6px; font-size: 12px; }
  th { text-align: left; font-size: 10px; text-transform: uppercase; letter-spacing: .05em; color: #64748b; border-bottom: 1px solid #e3e8f0; }
  th.n, td.n { text-align: right; font-variant-numeric: tabular-nums; }
  td { border-bottom: 1px solid #eef2f8; } td.b { font-weight: 700; }
  tr.tot td { border-top: 2px solid #cbd5e1; border-bottom: none; font-weight: 700; }
  .disc, .warn { margin-top: 8px; font-size: 11px; }
  .disc { color: #94a3b8; } .warn { color: #a16207; background: #fef9c3; border-radius: 6px; padding: 8px 10px; }
  .foot { margin-top: 22px; padding-top: 10px; border-top: 1px solid #e3e8f0; font-size: 10px; color: #94a3b8; }
  @media print { body { padding: 0; } .plan, tr { break-inside: avoid; } @page { margin: 14mm; } }
</style>
</head>
<body>
  <div class="hd">
    <div><div class="kicker">ELVA Insurance Summary</div><h1>${esc(opts.patientName)}</h1></div>
    <div class="gen">Generated ${esc(printedAt)}</div>
  </div>
  <hr />
  <h2>Insurance plans</h2>
  ${plans}
  ${cob}
  ${estimate}
  ${treatment}
  <div class="foot">Insurance benefits are estimates and not a guarantee of payment. Final benefits are determined by the payer after claim adjudication. Patient responsibility may change based on eligibility, remaining maximums, deductibles, coordination of benefits, plan limitations, and services received elsewhere.</div>
</body>
</html>`;
}

export function printInsuranceSummary(
  bundle: PiV2Bundle,
  opts: PrintInsuranceSummaryOptions,
): void {
  if (typeof window === 'undefined') return;
  const url = URL.createObjectURL(
    new Blob([buildInsuranceSummaryDoc(bundle, opts)], { type: 'text/html' }),
  );
  const w = window.open(url, '_blank', 'width=900,height=1100');
  if (!w) {
    URL.revokeObjectURL(url);
    window.print();
    return;
  }
  window.setTimeout(() => {
    w.focus();
    w.print();
    URL.revokeObjectURL(url);
  }, 500);
}
